import { useId } from 'react';
import { Gamepad2, Gift, MessageCircle, ShieldCheck } from 'lucide-react';

/**
 * Welcome mascot: a friendly escrow-shield character with marketplace icons
 * floating around it.
 *
 * The character is hand-drawn SVG (no image request, so it can never 404 and it
 * recolours with the theme). The floating chips are real DOM elements rather
 * than SVG so they inherit the shadow/spacing tokens and stay crisp.
 *
 * MOTION POLICY - this composition owns exactly ONE animation:
 *   • the character floats on a composited <div> wrapper (6s, transform only,
 *     see `.animate-z2-mascot-float` in index.css);
 *   • the chips, the ring and the eyes are static.
 * It used to run four infinite chip floats + an infinite ring pulse + an
 * infinite blink on top of the float - 7 perpetual animations inside the
 * welcome modal, several of them animating SVG children (which Chromium cannot
 * composite, so every frame re-rasterised the artwork). That is what read as
 * jitter on a phone.
 *
 * Gradient ids are per-instance (`useId`): two mascots are mounted at once
 * (empty state + welcome modal). With a fixed id the later one resolved its
 * paint server to the FIRST instance in the document, and on a phone that first
 * instance is the empty-state copy sitting in a `hidden` pane - so the modal's
 * shield body silently vanished and only the white face plate painted.
 */
const FLOATERS = [
  { Icon: Gamepad2, className: 'left-0 top-8' },
  { Icon: Gift, className: 'right-0 top-14' },
  { Icon: ShieldCheck, className: 'left-3 bottom-10' },
  { Icon: MessageCircle, className: 'right-2 bottom-4' },
];

export default function ChatMascot({ className = 'h-[186px] w-[252px]', paused = false }) {
  // React's id contains colons; keep the fragment identifier to word chars.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const shieldId = `z2m-shield-${uid}`;
  const rimId = `z2m-rim-${uid}`;
  const innerId = `z2m-inner-${uid}`;

  return (
    <div
      className={`z2-mascot-root relative grid place-items-center ${className}`}
      aria-hidden="true"
    >
      {/* Ambient brand glow behind the character. Static: a blurred layer is a
          one-off raster, and animating it (or anything over it) would repaint
          it every frame. */}
      <div className="z2-mascot-glow pointer-events-none absolute inset-0 scale-110 blur-2xl" />

      {/* Decorative chips - deliberately motionless. */}
      {FLOATERS.map(({ Icon, className: position }, index) => (
        <span
          key={index}
          className={`z2-mascot-chip pointer-events-none absolute z-10 grid h-11 w-11 place-items-center rounded-2xl border border-line bg-surface shadow-soft ${position}`}
        >
          <Icon size={18} className="text-accent" />
        </span>
      ))}

      {/* The character. The float lives on this HTML wrapper so the compositor
          can own the transform; `paused` freezes it while a modal has the
          screen (it resumes mid-cycle afterwards).
          `absolute inset-0` (not `h-full`) so the box is definite: with a
          percentage height the SVG resolves its own aspect ratio against the
          width instead and overflows the box by ~24px. The SVG then fills that
          box and the default preserveAspectRatio="xMidYMid meet" fits the
          260x230 artwork inside it, so the drawing never bleeds into the copy
          below. Callers must pass a sized box (see the default above). */}
      <div
        className={`z2-mascot-float animate-z2-mascot-float absolute inset-0 ${
          paused ? 'z2-motion-paused' : ''
        }`}
      >
        <svg
          viewBox="0 0 260 230"
          className="h-full w-full"
          role="img"
          aria-label="Friendly Z2Market chat mascot"
        >
          <defs>
            <linearGradient id={shieldId} x1="0" y1="0" x2="0.9" y2="1">
              {/* Colours come from the brand tokens, so a re-brand re-colours the mascot. */}
              <stop offset="0%" className="z2-shield-stop-1" />
              <stop offset="55%" className="z2-shield-stop-2" />
              <stop offset="100%" className="z2-shield-stop-3" />
            </linearGradient>
            <linearGradient id={rimId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
              <stop offset="60%" stopColor="#ffffff" stopOpacity="0.05" />
            </linearGradient>
            <radialGradient id={innerId} cx="0.5" cy="0.32" r="0.75">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Static halo ring - it used to pulse forever. */}
          <circle cx="130" cy="118" r="86" fill="none" strokeWidth="2" className="z2-mascot-ring" />

          {/* Soft contact shadow */}
          <ellipse cx="130" cy="212" rx="62" ry="9" className="z2-mascot-shadow" />

          {/* Shield body */}
          <path
            d="M130 18 C130 18 198 42 198 42 C198 42 198 108 198 108 C198 156 166 186 130 202 C94 186 62 156 62 108 L62 42 C62 42 130 18 130 18 Z"
            fill={`url(#${shieldId})`}
          />
          <path
            d="M130 18 C130 18 198 42 198 42 C198 42 198 108 198 108 C198 156 166 186 130 202 C94 186 62 156 62 108 L62 42 C62 42 130 18 130 18 Z"
            fill={`url(#${innerId})`}
          />
          {/* Rim highlight */}
          <path
            d="M130 18 C130 18 198 42 198 42 C198 42 198 108 198 108 C198 156 166 186 130 202 C94 186 62 156 62 108 L62 42 C62 42 130 18 130 18 Z"
            fill="none"
            stroke={`url(#${rimId})`}
            strokeWidth="3"
          />

          {/* Sparkle accent */}
          <g transform="translate(178 60) scale(0.9)" opacity="0.9">
            <path
              d="M0 -9 L2.6 -2.6 L9 0 L2.6 2.6 L0 9 L-2.6 2.6 L-9 0 L-2.6 -2.6 Z"
              fill="#ffffff"
              fillOpacity="0.9"
            />
          </g>

          {/* Face plate */}
          <ellipse cx="130" cy="112" rx="50" ry="46" fill="#ffffff" fillOpacity="0.94" />

          {/* Eyes - no blink animation: a 5.5s twitch is the first thing you
              notice on a static modal, and it forced an SVG repaint. */}
          <g>
            <ellipse cx="110" cy="104" rx="7.5" ry="10" fill="#3b1d78" />
            <ellipse cx="150" cy="104" rx="7.5" ry="10" fill="#3b1d78" />
            <circle cx="112.6" cy="100" r="2.6" fill="#ffffff" />
            <circle cx="152.6" cy="100" r="2.6" fill="#ffffff" />
          </g>

          {/* Cheeks */}
          <ellipse cx="96" cy="124" rx="7" ry="4.6" fill="#c4b5fd" opacity="0.85" />
          <ellipse cx="164" cy="124" rx="7" ry="4.6" fill="#c4b5fd" opacity="0.85" />

          {/* Smile */}
          <path
            d="M112 126 Q130 144 148 126"
            fill="none"
            stroke="#3b1d78"
            strokeWidth="5"
            strokeLinecap="round"
          />

          {/* Collar badge - escrow check */}
          <g transform="translate(130 170)">
            <circle r="17" fill="#ffffff" fillOpacity="0.16" />
            <circle r="12.5" fill="#ffffff" fillOpacity="0.22" />
            <path
              d="M-5.5 0 L-1.5 4.2 L6 -3.6"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}
